import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Camera, Mic, MapPin, Bell, ChevronDown, ChevronUp } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Permission {
  id: string;
  icon: React.ComponentType<any>;
  title: string;
  description: string;
  reason: string;
  granted: boolean;
}

interface PermissionsRequestProps {
  onComplete: () => void;
  onSkip: () => void;
}

export const PermissionsRequest = ({ onComplete, onSkip }: PermissionsRequestProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([
    {
      id: "camera",
      icon: Camera,
      title: t("onboarding.permissions.cameraTitle", { defaultValue: "Camera" }),
      description: t("onboarding.permissions.cameraDesc", { defaultValue: "Capture photos of your farm activities" }),
      reason: t("onboarding.permissions.cameraReason", { defaultValue: "We need camera access to let you quickly document your daily farm work through photos. This is essential for building your activity log and credit score." }),
      granted: false
    },
    {
      id: "microphone",
      icon: Mic,
      title: t("onboarding.permissions.microphoneTitle", { defaultValue: "Microphone" }),
      description: t("onboarding.permissions.microphoneDesc", { defaultValue: "Record voice notes for activities" }),
      reason: t("onboarding.permissions.microphoneReason", { defaultValue: "Voice commands and notes make logging activities hands-free and faster, especially when you're working on the farm. You can speak your activity details instead of typing." }),
      granted: false
    },
    {
      id: "location",
      icon: MapPin,
      title: t("onboarding.permissions.locationTitle", { defaultValue: "Location" }),
      description: t("onboarding.permissions.locationDesc", { defaultValue: "Enable regional features and weather" }),
      reason: t("onboarding.permissions.locationReason", { defaultValue: "Location helps us provide you with relevant local farming tips, connect you with nearby farmers, and give you location-specific weather updates and farming advice." }),
      granted: false
    },
    {
      id: "notifications",
      icon: Bell,
      title: t("onboarding.permissions.notificationsTitle", { defaultValue: "Notifications" }),
      description: t("onboarding.permissions.notificationsDesc", { defaultValue: "Receive reminders and updates" }),
      reason: t("onboarding.permissions.notificationsReason", { defaultValue: "Notifications help you stay on track with activity reminders, score updates, challenges, and important farming tips. You can customize what notifications you receive in settings." }),
      granted: false
    }
  ]);

  const handleAllow = async (id: string) => {
    // In a real app, this would request actual permissions
    setPermissions(prev =>
      prev.map(p => p.id === id ? { ...p, granted: true } : p)
    );
    
    toast({
      title: t("onboarding.permissions.granted", { defaultValue: "Permission Granted" }),
      description: t("onboarding.permissions.accessEnabled", { defaultValue: "{{title}} access enabled", title: permissions.find(p => p.id === id)?.title }),
    });
  };

  const toggleExpanded = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const allGranted = permissions.every(p => p.granted);
  const grantedCount = permissions.filter(p => p.granted).length;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="px-6 pt-6 pb-4">
        <h2 className="text-2xl font-bold text-foreground mb-2">
          {t("onboarding.permissions.title", { defaultValue: "Permissions" })}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t("onboarding.permissions.subtitle", { defaultValue: "Grant permissions to get the best experience" })}
        </p>
        {grantedCount > 0 && (
          <p className="text-xs text-primary mt-2">
            {t("onboarding.permissions.progress", { defaultValue: "{{count}} of {{total}} granted", count: grantedCount, total: permissions.length })}
          </p>
        )}
      </div>

      {/* Permissions List */}
      <div className="flex-1 px-6 py-4 overflow-y-auto space-y-3">
        {permissions.map((permission) => {
          const Icon = permission.icon;
          const isExpanded = expandedId === permission.id;
          
          return (
            <Card 
              key={permission.id}
              className={`p-4 transition-all ${
                permission.granted ? "border-primary bg-primary/5" : ""
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  permission.granted ? "bg-primary/20" : "bg-muted"
                }`}>
                  <Icon className={`w-6 h-6 ${
                    permission.granted ? "text-primary" : "text-muted-foreground"
                  }`} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <h3 className="font-semibold text-foreground">
                        {permission.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {permission.description}
                      </p>
                    </div>
                    
                    {!permission.granted && (
                      <Button
                        onClick={() => handleAllow(permission.id)}
                        size="sm"
                        variant="primary"
                        className="flex-shrink-0"
                      >
                        {t("onboarding.permissions.allow", { defaultValue: "Allow" })}
                      </Button>
                    )}
                  </div>

                  {/* Why We Need This */}
                  <button
                    onClick={() => toggleExpanded(permission.id)}
                    className="flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    {t("onboarding.permissions.whyNeeded", { defaultValue: "Why we need this" })}
                    {isExpanded ? (
                      <ChevronUp className="w-3 h-3" />
                    ) : (
                      <ChevronDown className="w-3 h-3" />
                    )}
                  </button>

                  {isExpanded && (
                    <p className="mt-2 text-xs text-muted-foreground animate-fade-in">
                      {permission.reason}
                    </p>
                  )}

                  {permission.granted && (
                    <p className="mt-2 text-xs text-primary font-medium">
                      ✓ {t("onboarding.permissions.grantedStatus", { defaultValue: "Granted" })}
                    </p>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Actions */}
      <div className="px-6 pb-8 space-y-3">
        <Button
          onClick={onComplete}
          variant="primary"
          size="lg"
          className="w-full"
          disabled={!allGranted}
        >
          {allGranted ? t("common.continue", { defaultValue: "Continue" }) : t("onboarding.permissions.grantAll", { defaultValue: "Grant All ({{count}}/{{total}})", count: grantedCount, total: permissions.length })}
        </Button>
        
        <Button onClick={onSkip} variant="ghost" size="lg" className="w-full">
          {t("onboarding.permissions.notNow", { defaultValue: "Not Now" })}
        </Button>

        <p className="text-xs text-center text-muted-foreground">
          {t("onboarding.permissions.changeAnytime", { defaultValue: "You can change these permissions anytime in Settings" })}
        </p>
      </div>
    </div>
  );
};
